import React, { useState, useEffect, Fragment } from "react";
import {
  Card,
  Button,
  Form,
  Spinner,
  InputGroup,
  Row,
  Col,
  Modal,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";
import {
  getUsers,
  createUser,
  editUser,
  fetchOrganizationDetails,
} from "ServerAPI/serverAPI";
import CommonHeader from "layouts/dashboard/CommonHeader";
import courseIcon from "../../assets/images/Courses.svg";
import { useCookies } from "react-cookie";
import editIcon from "../../components/organisation/edit.svg";
import TanstackTable from "../../components/elements/advance-table/TanstackTable.js";

const ViewOrganizationDetails = () => {
  const location = useLocation();
  const organization = location.state?.organization || {};

  console.log("📌 Received Organization Data:", organization); // Debugging log

  const [loadingCoordinators, setLoadingCoordinators] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [organizationCoordinators, setOrganizationCoordinators] = useState([]);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [activeTab, setActiveTab] = useState("singleUser");
  const [cookies] = useCookies();
  const coordinators = organization?.coordinators || [];
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [organizationDetails, setOrganizationDetails] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    roleId: "organizationCoordinator",
    phoneNumber: "",
    department: "",
    authorUserId: cookies.userId || "",
    organizationId: organization?.organizationId || "",
  });

  const handleDelete = (coordinator) => {
    console.log("Deleting:", coordinator);
    // Implement Delete functionality
  };

  const handleShowAddOrgModal = () => setShowAddOrgModal(true);
  const handleCloseAddOrgModal = () => setShowAddOrgModal(false);

  const getOrganizationDetails = async () => {
    const details = await fetchOrganizationDetails(
      organization?.organizationId
    );
    setOrganizationDetails(details?.data);
  };

  useEffect(() => {
    getOrganizationDetails();
  }, [organization]);

  const fetchUsers = async () => {
    try {
      setLoadingCoordinators(true);
      const response = await getUsers(
        cookies.email,
        cookies.roleId,
        organization.organizationId
      );
      setOrganizationCoordinators(response.data.users || []);
    } catch (error) {
      console.error("❌ Error fetching coordinators:", error);
      setOrganizationCoordinators([]);
    } finally {
      setLoadingCoordinators(false);
    }
  };

  useEffect(() => {
    if (organization?.organizationId) {
      fetchUsers();
    }
  }, [organization?.organizationId]);

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      formData.password =
        organizationDetails.userManagementDetails.name != "Azure SSO" 
          ? "admin"
          : false;
      const response = await createUser(formData);

      if (response.status === 201) {
        handleCloseAddOrgModal();
        fetchUsers();
      } else {
        console.error("❌ Error in API Response:", response);
      }
    } catch (error) {
      console.error("❌ Error creating organization:", error);
    } finally {
      setIsLoading(false); // ✅ Stop loading after API response
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // ✅ Keep spaces as user types
    }));
  };

  const handleEdit = (coordinator) => {
    console.log("Editing:", coordinator);
    setSelectedCoordinator(coordinator);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setSelectedCoordinator(null);
    setShowEditModal(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedCoordinator((prevState) => ({
      ...prevState,
      [name]: value.trim(),
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedCoordinator) return;

    try {
      console.log("🚀 Updating User Data:", selectedCoordinator);

      const response = await editUser({
        userId: selectedCoordinator.userId,
        name: selectedCoordinator.name,
        email: selectedCoordinator.email,
        phoneNumber: selectedCoordinator.phoneNumber,
        authorUserId: cookies.userId,
      });

      if (response.status === 200) {
        console.log("✅ User Updated Successfully:", response.data);

        // ✅ Update the coordinators list in state instead of reloading
        setOrganizationCoordinators((prevCoordinators) =>
          prevCoordinators.map((coordinator) =>
            coordinator.userId === selectedCoordinator.userId
              ? { ...coordinator, ...selectedCoordinator } // Update the selected coordinator
              : coordinator
          )
        );

        setShowEditModal(false); // Close modal after updating
      } else {
        console.error("❌ Error in API Response:", response);
      }
    } catch (error) {
      console.error("❌ Error updating user:", error);
    }
  };

  const columns = [
    {
      header: <span style={{ color: "#224B99", fontWeight: "bold" }}>SN</span>,
      accessorKey: "id",
      cell: (info) => info.row.index + 1,
    },
    {
      header: (
        <span style={{ color: "#224B99", fontWeight: "bold" }}>
          Organization Co-Ordinator
        </span>
      ),
      accessorKey: "name",
    },
    {
      header: (
        <span style={{ color: "#224B99", fontWeight: "bold" }}>Email</span>
      ),
      accessorKey: "email",
    },
    {
      header: (
        <span style={{ color: "#224B99", fontWeight: "bold" }}>
          Phone Number
        </span>
      ),
      accessorKey: "phoneNumber",
      cell: (info) => info.getValue() || "N/A",
    },
    {
      header: (
        <span style={{ color: "#224B99", fontWeight: "bold" }}>Actions</span>
      ),
      accessorKey: "actions",
      cell: ({ row }) => (
        <div>
          {/* Edit Icon */}
          <img
            src={editIcon}
            alt="Edit"
            style={styles.icon}
            onClick={() => handleEdit(row.original)}
          />

          {/* <img
            src={editIcon}
            alt="Edit"
            style={styles.icon}
            onClick={() => handleEdit(row.original)}
          /> */}
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <CommonHeader
        text="Organization Onboarding"
        subText="You can add & manage organization details here"
        icon={courseIcon}
      />

      <div className="container mt-4">
        <Card
          className="p-4 shadow-sm"
          style={{ backgroundColor: "#f8f9fa", borderRadius: "10px" }}
        >
          <Card.Body>
            <Card
              className="p-4 shadow-sm mb-4"
              style={{ backgroundColor: "#f1f3f5", borderRadius: "10px" }}
            >
              <Card.Body
                style={{
                  borderRadius: "15px",
                  borderColor: "#000000",
                  backgroundColor: "#f1f3f5",
                  padding: "20px",
                }}
              >
                <h4 className="mb-4">
                  <strong>Organization Details</strong>
                </h4>

                <div style={styles.organizationDetailsContainer}>
                  <div style={styles.detailBox}>
                    <strong>Organization Name</strong>
                    <br />{" "}
                    {organization?.organizationName ||
                      organization?.name ||
                      "N/A"}
                  </div>

                  <div style={styles.separatorVertical}></div>

                  <div style={styles.detailBox}>
                    <strong>Company Address</strong>
                    <br />
                    <div>
                      {organization?.companyAddress ||
                        organization?.address ||
                        "N/A"}
                    </div>
                  </div>

                  <div style={styles.separatorVertical}></div>
                  <div style={styles.detailBox}>
                    <strong>User Management</strong>
                    <br />{" "}
                    {organizationDetails?.userManagementDetails?.name || "N/A"}
                  </div>

                  <div style={styles.separatorVertical}></div>

                  <div style={styles.detailBox}>
                    <strong>Gen AI Model</strong>
                    <br /> {organization?.genAIModel || "N/A"}
                  </div>
                </div>

                {/* Search Input and Add Button in the Same Line */}
                <Row className="mb-3 align-items-center">
                  {/* Search Input with Reduced Width */}
                  <Col md={8} className="d-flex">
                    <InputGroup style={{ maxWidth: "300px" }}>
                      <Form.Control
                        type="text"
                        placeholder="Search Co-Ordinator"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>

                  {/* Add Organization Button Aligned to the Right */}
                  <Col md={4} className="text-end">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleShowAddOrgModal}
                      disabled={isLoading}
                    >
                      {isLoading ? "Loading..." : "+ Add "}
                    </Button>
                  </Col>
                </Row>

                {loadingCoordinators ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: "200px" }}
                  >
                    <Spinner animation="border" size="lg" />
                  </div>
                ) : (
                  <TanstackTable
                    data={organizationCoordinators.filter((coord) =>
                      coord.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    )}
                    columns={columns}
                    filter={false} // Disable built-in filter since we added our own search input
                    pagination={true}
                    filterPlaceholder="Search Coordinators..."
                  />
                )}
              </Card.Body>
            </Card>

            {/* Organization Coordinators Section */}
            <Card
              className="shadow-sm"
              style={{ backgroundColor: "#f1f3f5", borderRadius: "10px" }}
            ></Card>
          </Card.Body>
        </Card>
      </div>

      {/* ✅ Add Organization Modal */}
      <Modal show={showAddOrgModal} onHide={handleCloseAddOrgModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Organization Coordinator</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="singleUser" title="Single User">
              <Form onSubmit={handleCreateOrganization}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter Name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter Email"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="Enter Phone Number"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCloseAddOrgModal}
                    className="me-2"
                    style={{
                      border: "2px solid #6c757d",
                      padding: "8px 24px",
                      borderRadius: "5px",
                      fontWeight: "bold",
                      backgroundColor: "white",
                      color: "#6c757d",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isLoading} // ✅ Disable when loading
                    style={{
                      backgroundColor: "#224B99",
                      border: "2px solid #224B99",
                      padding: "8px 24px",
                      borderRadius: "5px",
                      fontWeight: "bold",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isLoading ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Form>
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>

      {/* ✅ Edit Organization Coordinator Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Organization Coordinator</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={selectedCoordinator?.name || ""}
                    onChange={handleEditInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={selectedCoordinator?.email || ""}
                    onChange={handleEditInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="phoneNumber"
                    value={selectedCoordinator?.phoneNumber || ""}
                    onChange={handleEditInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleCloseEditModal}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveEdit}>
                Save{" "}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

const styles = {
  organizationDetailsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "15px",
    padding: "10px",
  },
  detailBox: {
    textAlign: "center",
    padding: "10px",
    flex: 1,
    minWidth: "150px",
    fontSize: "14px",
    wordWrap: "break-word",
    whiteSpace: "pre-wrap", // Ensures multi-line address support
  },
  separatorVertical: {
    width: "2px",
    height: "60px",
    backgroundColor: "#ccc", // Light grey separator
    alignSelf: "center",
  },
  courseList: {
    paddingLeft: "15px", // Adds indentation for bullet list
    listStyleType: "disc",
  },
};

export default ViewOrganizationDetails;
